/**
 * MapLibre Layer Style Definitions for the Obstruction Symbol Layer
 *
 * Two stacked layers attached to the unified `map-cluster-source`:
 *
 *   1. Severity halo — a small circle behind the icon, coloured by severity
 *      (gray → yellow → orange → red). Reads as a "danger ring" around the
 *      anchor when an obstruction has Medium+ severity.
 *   2. Anchor symbol — composite SDF sprite (disc with icon cutout) tinted
 *      by status hex, sprite selected by a `match` expression on
 *      `['get', 'type']`.
 *
 * Filters are `featureKind === 'obstruction'` so the same source can host
 * task / note / obstruction features side by side.
 *
 * @module components/map/UnifiedAnchorSymbolLayer/styles/obstructionSymbolLayerStyles
 */

import type {
  CircleLayerSpecification,
  SymbolLayerSpecification,
  ExpressionSpecification,
} from '@maplibre/maplibre-gl-style-spec';

import {
  ZOOM_SCALE_MIN,
  ZOOM_SCALE_MAX,
  SCALE_MIN as SCALE_FACTOR_MIN,
  SCALE_MAX as SCALE_FACTOR_MAX,
} from '@/utils/markerScaling';
import { SHAPE_SPRITE_SIZE } from '../../symbolSprites';
import {
  ObstructionType,
  SeverityLevel,
  ObstructionStatus,
} from '@/types/obstructions';
import {
  OBSTRUCTION_SEVERITY_HEX_COLORS,
  OBSTRUCTION_STATUS_HEX_COLORS,
  OBSTRUCTION_TYPE_SPRITE_KEYS,
} from '@/constants/obstructionMapColors';
import {
  MAP_CLUSTER_FEATURE_KIND_OBSTRUCTION,
  buildUnclusteredFeatureFilter,
} from '../../MapClusterLayer/featureKind';

// ===== LAYER IDENTIFIERS =====

export const OBSTRUCTION_LAYER_IDS = {
  HALO: 'obstruction-anchor-halo',
  SYMBOL: 'obstruction-anchor-symbol-layer',
} as const;

// ===== FILTER =====

const unclusteredObstructionFilter = buildUnclusteredFeatureFilter(
  MAP_CLUSTER_FEATURE_KIND_OBSTRUCTION
);

// ===== SIZE CONSTANTS =====

/**
 * Base icon size for an obstruction marker (CSS pixels). Slightly smaller
 * than the task marker (36px) so the severity halo can poke out behind it
 * without occluding adjacent markers.
 */
const OBSTRUCTION_BASE_SIZE_PX = 32;
const OBSTRUCTION_SIZE_RATIO = OBSTRUCTION_BASE_SIZE_PX / SHAPE_SPRITE_SIZE;

const OBSTRUCTION_ICON_SIZE_MIN = SCALE_FACTOR_MIN * OBSTRUCTION_SIZE_RATIO;
const OBSTRUCTION_ICON_SIZE_MAX = SCALE_FACTOR_MAX * OBSTRUCTION_SIZE_RATIO;

/** Halo radius (CSS pixels) — sits just outside the icon disc. */
const HALO_RADIUS_MIN_PX = 10;
const HALO_RADIUS_MAX_PX = 18;
const HALO_STROKE_WIDTH_PX = 2;
const HALO_FILL_OPACITY = 0.18;
const HALO_STROKE_OPACITY = 0.65;

// ===== ICON IMAGE EXPRESSION =====

/**
 * Resolves the sprite key from `['get', 'type']`. Falls back to the
 * Maintenance sprite when the value is unknown so the layer never renders
 * an empty icon.
 */
const obstructionIconImageExpression: ExpressionSpecification = [
  'match',
  ['get', 'type'],
  ObstructionType.Maintenance,
  OBSTRUCTION_TYPE_SPRITE_KEYS[ObstructionType.Maintenance],
  ObstructionType.Construction,
  OBSTRUCTION_TYPE_SPRITE_KEYS[ObstructionType.Construction],
  ObstructionType.Accident,
  OBSTRUCTION_TYPE_SPRITE_KEYS[ObstructionType.Accident],
  ObstructionType.Closure,
  OBSTRUCTION_TYPE_SPRITE_KEYS[ObstructionType.Closure],
  ObstructionType.Event,
  OBSTRUCTION_TYPE_SPRITE_KEYS[ObstructionType.Event],
  ObstructionType.Emergency,
  OBSTRUCTION_TYPE_SPRITE_KEYS[ObstructionType.Emergency],
  OBSTRUCTION_TYPE_SPRITE_KEYS[ObstructionType.Maintenance],
];

const obstructionIconSizeExpression: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  ZOOM_SCALE_MIN,
  OBSTRUCTION_ICON_SIZE_MIN,
  ZOOM_SCALE_MAX,
  OBSTRUCTION_ICON_SIZE_MAX,
];

// ===== COLOR EXPRESSIONS =====

/**
 * Severity-driven halo color expression. Uses the WebGL hex palette from
 * `obstructionMapColors.ts` (mirrors the Mantine `gray-6/yellow-6/orange-6/red-6`
 * tokens used by the obstruction badges).
 */
const severityHaloColorExpression: ExpressionSpecification = [
  'match',
  ['get', 'severity'],
  SeverityLevel.Low,
  OBSTRUCTION_SEVERITY_HEX_COLORS[SeverityLevel.Low],
  SeverityLevel.Medium,
  OBSTRUCTION_SEVERITY_HEX_COLORS[SeverityLevel.Medium],
  SeverityLevel.High,
  OBSTRUCTION_SEVERITY_HEX_COLORS[SeverityLevel.High],
  SeverityLevel.Critical,
  OBSTRUCTION_SEVERITY_HEX_COLORS[SeverityLevel.Critical],
  OBSTRUCTION_SEVERITY_HEX_COLORS[SeverityLevel.Low],
];

/**
 * Status-driven icon disc tint. Falls back to the Planned color when the
 * feature property is missing.
 */
const statusColorExpression: ExpressionSpecification = [
  'match',
  ['get', 'status'],
  ObstructionStatus.Planned,
  OBSTRUCTION_STATUS_HEX_COLORS[ObstructionStatus.Planned],
  ObstructionStatus.Scheduled,
  OBSTRUCTION_STATUS_HEX_COLORS[ObstructionStatus.Scheduled],
  ObstructionStatus.Active,
  OBSTRUCTION_STATUS_HEX_COLORS[ObstructionStatus.Active],
  ObstructionStatus.Extended,
  OBSTRUCTION_STATUS_HEX_COLORS[ObstructionStatus.Extended],
  ObstructionStatus.Completed,
  OBSTRUCTION_STATUS_HEX_COLORS[ObstructionStatus.Completed],
  ObstructionStatus.Archived,
  OBSTRUCTION_STATUS_HEX_COLORS[ObstructionStatus.Archived],
  OBSTRUCTION_STATUS_HEX_COLORS[ObstructionStatus.Planned],
];

const haloRadiusExpression: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  ZOOM_SCALE_MIN,
  HALO_RADIUS_MIN_PX,
  ZOOM_SCALE_MAX,
  HALO_RADIUS_MAX_PX,
];

// ===== LAYER SPECS =====

/** Severity halo — rendered BELOW the symbol so the icon stays the dominant
 *  visual element. Halo + ring fade in/out via zoom interpolation. */
export const obstructionHaloLayer: Omit<CircleLayerSpecification, 'source'> = {
  id: OBSTRUCTION_LAYER_IDS.HALO,
  type: 'circle',
  filter: unclusteredObstructionFilter,
  paint: {
    'circle-color': severityHaloColorExpression,
    'circle-radius': haloRadiusExpression,
    'circle-opacity': HALO_FILL_OPACITY,
    'circle-stroke-color': severityHaloColorExpression,
    'circle-stroke-width': HALO_STROKE_WIDTH_PX,
    'circle-stroke-opacity': HALO_STROKE_OPACITY,
  },
};

/** Anchor icon — SDF composite, tinted by status. */
export const obstructionSymbolLayer: Omit<SymbolLayerSpecification, 'source'> =
  {
    id: OBSTRUCTION_LAYER_IDS.SYMBOL,
    type: 'symbol',
    filter: unclusteredObstructionFilter,
    layout: {
      'icon-image': obstructionIconImageExpression,
      'icon-size': obstructionIconSizeExpression,
      'icon-allow-overlap': true,
      'icon-anchor': 'center',
      'icon-padding': 0,
    },
    paint: {
      'icon-color': statusColorExpression,
    },
  };
